import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import {
  Users, Calendar, Heart, MapPin, UserCheck,
  ChevronLeft, ChevronRight, CheckCircle2, AlertCircle, Sparkles,
} from 'lucide-react';

export const COACH_KEY = 'mg_browse_coach_done';

const buildSteps = (lang) => {
  const tr = (en, id) => (lang === 'en' ? en : id);
  return [
    {
      icon: Users,
      tint: '#C7867A',
      pill: tr('Step 1 of 5 — Most important', 'Langkah 1 dari 5 — Paling penting'),
      title: tr('Filter Gender first!', 'Filter Jenis Kelamin dulu!'),
      body: tr(
        'Start by setting the Gender filter (Male / Female). This is the MOST IMPORTANT filter — without it you will see members of all genders.',
        'Mulai dengan mengatur filter Jenis Kelamin (Pria / Wanita). Ini filter PALING PENTING — tanpa ini kamu akan melihat anggota dari semua gender.'
      ),
      tip: tr(
        'Tip: Tap the "Semua" pill with the people icon at the bottom of the filter row, then pick Male or Female.',
        'Tips: Ketuk pil "Semua" dengan ikon orang di baris filter bawah, lalu pilih Pria atau Wanita.'
      ),
      mock: (
        <div className="flex items-center justify-center gap-2">
          <div className="px-3 py-2 rounded-full glass border-2 border-[#C7867A] text-[#C7867A] text-sm font-semibold flex items-center gap-1.5 shadow-md animate-pulse">
            <Users className="w-4 h-4" />
            <span>{tr('Female', 'Wanita')}</span>
          </div>
        </div>
      ),
    },
    {
      icon: Calendar,
      tint: '#F59E0B',
      pill: tr('Step 2 of 5', 'Langkah 2 dari 5'),
      title: tr('Then set an age range', 'Lalu atur rentang Usia'),
      body: tr(
        'Type a minimum and maximum age in the "Min Age" and "Max Age" boxes — e.g. 25 to 35. Leave blank to see all ages.',
        'Ketik usia minimum dan maksimum di kotak "Min Age" dan "Max Age" — misal 25 sampai 35. Kosongkan untuk melihat semua usia.'
      ),
      tip: tr(
        'Tip: You can leave only one of them empty — e.g. fill 25 only, to see anyone 25 or older.',
        'Tips: Boleh isi salah satu saja — misal isi 25 saja, untuk melihat usia 25 ke atas.'
      ),
      mock: (
        <div className="flex items-center justify-center gap-2">
          <div className="px-3 py-2 rounded-full glass border border-[var(--border-color)] text-sm">25</div>
          <span className="t-text-muted text-sm">→</span>
          <div className="px-3 py-2 rounded-full glass border border-[var(--border-color)] text-sm">35</div>
        </div>
      ),
    },
    {
      icon: Heart,
      tint: '#D98A94',
      pill: tr('Step 3 of 5', 'Langkah 3 dari 5'),
      title: tr('Pick your Match category', 'Pilih kategori Match'),
      body: tr(
        'Choose the matchmaking category that fits you: Buddhist Match, Catholic Match, Tenglang Match, etc. This narrows members to your specific group.',
        'Pilih kategori perjodohan yang cocok untuk Anda: Buddhist Match, Catholic Match, Tenglang Match, dll. Ini akan mempersempit anggota ke kelompok spesifik Anda.'
      ),
      tip: tr(
        'Tip: Tap the "Semua" pill with the heart icon at the top-left of the filter row.',
        'Tips: Ketuk pil "Semua" dengan ikon hati di kiri-atas baris filter.'
      ),
      mock: (
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <div className="px-3 py-2 rounded-full bg-[#D98A94]/10 border-2 border-[#D98A94] text-[#D98A94] text-sm font-semibold flex items-center gap-1.5">
            <Heart className="w-3.5 h-3.5" />
            <span>Buddhist Match</span>
          </div>
        </div>
      ),
    },
    {
      icon: MapPin,
      tint: '#10B981',
      pill: tr('Step 4 of 5', 'Langkah 4 dari 5'),
      title: tr('Filter by Location', 'Filter berdasarkan Lokasi'),
      body: tr(
        'Pick a city or province from the location filter. Useful if you want to meet someone nearby.',
        'Pilih kota atau provinsi dari filter lokasi. Berguna jika Anda ingin bertemu seseorang yang dekat.'
      ),
      tip: tr(
        'Tip: Tap the "Semua" pill with the map-pin icon at the top-right of the filter row.',
        'Tips: Ketuk pil "Semua" dengan ikon peta di kanan-atas baris filter.'
      ),
      mock: (
        <div className="flex items-center justify-center gap-2">
          <div className="px-3 py-2 rounded-full bg-[#10B981]/10 border-2 border-[#10B981] text-[#10B981] text-sm font-semibold flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5" />
            <span>Jakarta</span>
          </div>
        </div>
      ),
    },
    {
      icon: UserCheck,
      tint: '#0EA5E9',
      pill: tr('Step 5 of 5 — Finish', 'Langkah 5 dari 5 — Selesai'),
      title: tr('Claim Your Own Profile', 'Klaim Profil Anda Sendiri'),
      body: tr(
        'Don\'t forget to claim your own profile! Tap the "Profil" tab at the bottom, then "Claim Profile". This lets you edit your bio, photos, and contact info.',
        'Jangan lupa klaim profil Anda sendiri! Ketuk tab "Profil" di bawah, lalu "Klaim Profil". Ini memungkinkan Anda mengedit bio, foto, dan info kontak.'
      ),
      tip: tr(
        'Required: Without claiming, you cannot edit your own profile or show up in the "Suitable for You" suggestions.',
        'Wajib: Tanpa klaim, Anda tidak bisa edit profil sendiri atau muncul di saran "Cocok untuk Anda".'
      ),
      mock: (
        <div className="flex items-center justify-center gap-2">
          <div className="px-4 py-2 rounded-full bg-[#D98A94] text-white text-sm font-semibold flex items-center gap-1.5 shadow-lg">
            <UserCheck className="w-4 h-4" />
            <span>{tr('Claim Profile', 'Klaim Profil')}</span>
          </div>
        </div>
      ),
    },
  ];
};

export default function BrowseCoachmark({ open, onClose, lang = 'id' }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [understood, setUnderstood] = useState(false);
  const steps = buildSteps(lang);
  const current = steps[step];
  const Icon = current.icon;
  const isLast = step === steps.length - 1;

  const close = () => {
    setStep(0);
    setUnderstood(false);
    onClose();
  };

  const finish = () => {
    if (understood) localStorage.setItem(COACH_KEY, '1');
    close();
  };

  const goToProfile = () => {
    if (understood) localStorage.setItem(COACH_KEY, '1');
    onClose();
    setTimeout(() => navigate('/profile'), 100);
  };

  const tr = (en, id) => (lang === 'en' ? en : id);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent
        data-testid="browse-coachmark-dialog"
        className="glass-card t-text w-[calc(100vw-1rem)] max-w-md p-0 overflow-hidden rounded-2xl [&>button.absolute]:hidden"
      >
        <DialogTitle className="sr-only">{current.title}</DialogTitle>
        <DialogDescription className="sr-only">{current.body}</DialogDescription>

        {/* Header band */}
        <div
          className="px-5 py-4 flex items-center gap-3"
          style={{ background: `linear-gradient(135deg, ${current.tint}1f, ${current.tint}0a)` }}
        >
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-md"
            style={{ background: current.tint, color: '#fff' }}
          >
            <Icon className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: current.tint }}>{current.pill}</p>
            <h2 className="t-text text-base sm:text-lg font-bold leading-tight">{current.title}</h2>
          </div>
        </div>

        {/* Step progress dots */}
        <div className="flex items-center justify-center gap-1.5 pt-3">
          {steps.map((s, i) => (
            <button
              key={s.title}
              onClick={() => setStep(i)}
              data-testid={`coach-step-dot-${i}`}
              aria-label={`Go to step ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${i === step ? 'w-6' : 'w-1.5'} ${i <= step ? '' : 'opacity-30'}`}
              style={{ background: i <= step ? current.tint : 'var(--border-color)' }}
            />
          ))}
        </div>

        {/* Body */}
        <div className="px-5 py-5 space-y-4">
          <div className="flex justify-center py-2">{current.mock}</div>

          <p className="t-text-secondary text-sm leading-relaxed">{current.body}</p>

          <div
            className="flex items-start gap-2 px-3 py-2.5 rounded-lg border"
            style={{ background: `${current.tint}14`, borderColor: `${current.tint}55` }}
          >
            <Sparkles className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: current.tint }} />
            <p className="t-text text-[13px] leading-relaxed">{current.tip}</p>
          </div>

          {/* Final-step checkbox */}
          {isLast && (
            <label
              data-testid="coach-understood-label"
              className="flex items-start gap-2.5 p-3 rounded-xl border-2 cursor-pointer transition"
              style={{
                borderColor: understood ? current.tint : 'var(--border-color)',
                background: understood ? `${current.tint}14` : 'transparent',
              }}
            >
              <input
                type="checkbox"
                checked={understood}
                onChange={(e) => setUnderstood(e.target.checked)}
                data-testid="coach-understood-checkbox"
                className="mt-0.5 w-5 h-5 accent-[#0EA5E9] cursor-pointer flex-shrink-0"
              />
              <span className="t-text text-sm font-medium leading-snug">
                {tr('Yes, I understand. Don\'t show this again.', 'Ya, saya sudah memahami. Jangan tampilkan lagi.')}
              </span>
            </label>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t flex items-center gap-2" style={{ borderColor: 'var(--border-color)' }}>
          {step > 0 ? (
            <Button
              variant="outline"
              onClick={() => setStep(s => Math.max(0, s - 1))}
              data-testid="coach-back-btn"
              className="rounded-full px-4 t-border t-text-secondary"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              {tr('Back', 'Kembali')}
            </Button>
          ) : (
            <button
              onClick={close}
              data-testid="coach-skip-btn"
              className="t-text-muted text-sm px-3 py-2 hover:underline"
            >
              {tr('Skip', 'Lewati')}
            </button>
          )}
          <div className="flex-1" />
          {isLast ? (
            <div className="flex gap-2">
              <Button
                onClick={finish}
                data-testid="coach-finish-btn"
                className="rounded-full bg-[#D98A94] hover:bg-[#D98A94]/90 text-white px-5"
              >
                <CheckCircle2 className="w-4 h-4 mr-1.5" />
                {tr('Done', 'Selesai')}
              </Button>
              <Button
                onClick={goToProfile}
                data-testid="coach-go-profile-btn"
                className="rounded-full bg-[#0EA5E9] hover:bg-[#0EA5E9]/90 text-white px-5"
              >
                {tr('Go to Profile', 'Ke Profil')}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          ) : (
            <Button
              onClick={() => setStep(s => Math.min(steps.length - 1, s + 1))}
              data-testid="coach-next-btn"
              className="rounded-full bg-[#D98A94] hover:bg-[#D98A94]/90 text-white px-5"
            >
              {tr('Next', 'Lanjut')}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>

        {/* Bottom warning when checkbox not checked on final step */}
        {isLast && !understood && (
          <div className="px-5 pb-4 -mt-2">
            <div className="flex items-start gap-1.5 t-text-muted text-[11px] leading-snug">
              <AlertCircle className="w-3 h-3 flex-shrink-0 mt-0.5" />
              <span>{tr('Tap "Done" without the checkbox to see this guide again on your next visit.', 'Ketuk "Selesai" tanpa centang akan menampilkan panduan lagi pada kunjungan berikutnya.')}</span>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
